import React, { useEffect, useState } from "react";
import { faTriangleExclamation, faMultiply, faPenToSquare, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, Modal, Offcanvas } from "react-bootstrap";
import { IConstellationDefinition, IConstellationInfo } from "../../../model/CarbonExternalModels";
import { objectEqual } from "../../../common/functions/objectComparison";
import { initIConstellationDefinition } from "../../../store/constellations/constellationsUtils";
import { apiCreateConstellation, apiUpdateConstellation } from "../../../messages/api/gcmAPICallers";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { selectConstellations, useCarbonSelector } from "../../../store/carbonStore";
import { UserPermission } from "../../../model/CarbonPermissionModels";
import { Button } from "userful-chronos-common-ui/dist";

interface IProps {
    type: "PRIMARY" | "SECONDARY" | "DROPDOWN";
    constellation?: IConstellationDefinition;
    permissions: UserPermission;
}

export default function CreateConstellation(props: IProps) {
    const [showForm, setShowForm] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [registerChange, setRegisterChange] = useState<boolean>(false);

    const [originalConstellation, setOriginalConstellation] = useState<IConstellationDefinition>(
        props.constellation ? props.constellation : initIConstellationDefinition(),
    );
    const [currentConstellation, setCurrentConstellation] = useState<IConstellationDefinition>(originalConstellation);

    // Data
    const constellations: { [id: string]: IConstellationInfo } = useCarbonSelector(selectConstellations);
    const allConstellations: IConstellationInfo[] = [];
    for (const property in constellations) {
        allConstellations.push(constellations[property]);
    }

    const foundChanges = !objectEqual(originalConstellation, currentConstellation);

    const nameError =
        (allConstellations.find((c) => c.definition.name === currentConstellation.name) &&
            currentConstellation.name !== originalConstellation.name) ||
        currentConstellation.name.length === 0
            ? "Please make sure the name is unique and is not empty."
            : "";

    useEffect(() => {
        if (props.constellation) setOriginalConstellation(props.constellation);
    }, [props.constellation]);

    useEffect(() => {
        setCurrentConstellation(originalConstellation);
    }, [originalConstellation]);

    function handleNameChange(event) {
        setRegisterChange(true);
        setCurrentConstellation({ ...currentConstellation, name: event.target.value });
    }
    function handleDescriptionChange(event) {
        setRegisterChange(true);
        setCurrentConstellation({ ...currentConstellation, description: event.target.value });
    }
    const onFormShow = () => {
        setShowForm(true);
    };
    const onFormHide = () => {
        if (foundChanges) {
            setShowConfirmation(true);
        } else {
            onRestore();
            setShowForm(false);
        }
    };
    const onRestore = () => {
        setRegisterChange(false);
        if (props.constellation) {
            setOriginalConstellation(props.constellation);
            setCurrentConstellation(props.constellation);
        } else {
            setOriginalConstellation(initIConstellationDefinition());
        }
    };

    const onCreate = () => {
        if (props.constellation) {
            apiUpdateConstellation(getGlobalStates().keycloak.token, currentConstellation);
        } else {
            apiCreateConstellation(getGlobalStates().keycloak.token, currentConstellation);
        }
        onRestore();
        setShowForm(false);
    };

    // console.warn(props.permissions, props.type);
    if (!props.permissions.isConstellationCreator) return null;

    return (
        <>
            {props.type === "PRIMARY" && (
                <Button className="userful-responsive-button" variant="success" onClick={(e) => onFormShow()}>
                    <FontAwesomeIcon icon={faPlus} />
                    <p>Create Constellation</p>
                </Button>
            )}
            {props.type === "SECONDARY" && (
                <Button variant="secondary" onClick={(e) => onFormShow()}>
                    Create Constellation
                </Button>
            )}
            {props.type === "DROPDOWN" && (
                <Dropdown.Item className="dropdownItem" onClick={(e) => onFormShow()}>
                    <div className="gcm-icon-dropdown-menu-icon-container">
                        <FontAwesomeIcon icon={props.constellation ? faPenToSquare : faPlus} />
                    </div>
                    <p>{props.constellation ? "Edit" : "Create Constellation"}</p>
                </Dropdown.Item>
            )}

            <Offcanvas show={showForm} onHide={onFormHide} placement="end">
                <div className="gcm-form-Body">
                    <div className="gcm-form-Header">
                        <div className="gcm-form-Row">
                            <h1 className="gcm-form-HeaderTitle">
                                {props.constellation ? "Edit Constellation" : "Create Constellation"}
                            </h1>
                            <button className="userfulIconButton" onClick={onFormHide}>
                                <FontAwesomeIcon icon={faMultiply} />
                            </button>
                        </div>
                        <div className="gcm-form-Row">
                            <h3 className="gcm-form-HeaderDescription">
                                Constellations are used to group together related clusters.
                            </h3>
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">Constellation Details</h4>
                            <h5 className="gcm-form-ContentsDescription">
                                Details relating to this Constellation group.
                            </h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Name</h5>
                                <h6>{currentConstellation.name.length}/60</h6>
                            </div>
                            <input
                                className="userfulInputField"
                                placeholder="Enter name here..."
                                type="text"
                                maxLength={60}
                                value={currentConstellation.name}
                                onChange={handleNameChange}
                            />
                            {nameError && registerChange && <div className="errorText">{nameError}</div>}
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Description</h5>
                                <h6>{currentConstellation.description.length}/200</h6>
                            </div>
                            <textarea
                                className="uTextarea"
                                placeholder="Enter description here..."
                                maxLength={200}
                                value={currentConstellation.description}
                                onChange={handleDescriptionChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="gcm-form-Footer">
                    <button className="secondaryUserfulButton" onClick={onFormHide}>
                        Cancel
                    </button>
                    <button
                        className="primaryUserfulButton"
                        onClick={onCreate}
                        disabled={nameError.length > 0 || !foundChanges}
                    >
                        {props.constellation ? "Save Changes" : "Create"}
                    </button>
                </div>
            </Offcanvas>

            <Modal
                centered
                show={showConfirmation}
                onHide={() => {
                    setShowConfirmation(false);
                }}
            >
                <div className="gcm-pop-up-modal-wrapper">
                    <div className="gcm-pop-up-modal-wrapper-body">
                        <div className="gcm-pop-up-modal-wrapper-head">
                            <div className="gcm-pop-up-modal-wrapper-title">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <h1>Are you sure you want to leave?</h1>
                            </div>
                            <Button
                                variant="secondary"
                                iconOnly
                                onClick={() => {
                                    setShowConfirmation(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faMultiply} />
                            </Button>
                        </div>
                        <p>
                            Are you sure you want to leave without saving your changes? Any unsaved modifications will
                            be lost.
                        </p>
                    </div>
                    <div className="confirmationDialogHorizontalWrapper reversed">
                        <Button
                            variant="danger"
                            onClick={(e) => {
                                setShowForm(false);
                                setShowConfirmation(false);
                                onRestore();
                            }}
                        >
                            Confirm
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={(e) => {
                                setShowConfirmation(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
